import { Box } from "@chakra-ui/core";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { RootState } from "../../redux/store";
import {
  APP_DOWNLOAD_PATH,
  hidePromoForDays,
  isMobileViewport,
  isPromoHidden,
} from "../../services/app-download.service";
import { shouldAskConsent } from "../../services/competition-consent.service";
import { useCompetitionConsentStore } from "../navbar/NavConstants";
import AppPromoBar from "./AppPromoBar";
import AppPromoModal from "./AppPromoModal";

const MODAL_HIDDEN_KEY = "appPromoModalHiddenUntil";
const BAR_HIDDEN_KEY = "appPromoBarHiddenUntil";
// Bezárás után egy hétig csend; aki a store-ba kattintott, azt sokáig nem
// zavarjuk (valószínűleg már letöltötte).
const DISMISS_DAYS = 7;
const STORE_CLICK_DAYS = 30;
const MODAL_DELAY_MS = 2500;

// A rendelés folyamatát nem szakítjuk félbe: a kosárban és a fizetés utáni
// oldalakon nincs se ablak, se sáv (a sikeres rendelés oldalán a
// ConfirmationScreen saját ajánlója van — „Verzió 5").
const EXCLUDED_SUBPAGES = [
  "kosar",
  "cart",
  "success",
  "cancelled",
  "declined",
];

function isPromoRoute(pathname: string): boolean {
  if (pathname === "/" || pathname.startsWith(APP_DOWNLOAD_PATH)) return false;
  const subpage = pathname.split("/")[2];
  return !EXCLUDED_SUBPAGES.includes(subpage);
}

// App-letöltés ösztönző: asztalon felugró ablak (Verzió 2 + QR), mobilon
// tapadó sáv (Verzió 3). Az App szintjén él, így oldalváltáskor nem nyílik újra.
function AppPromo(): JSX.Element | null {
  const location = useLocation();
  const isLoggedIn = useSelector(
    (state: RootState) => state.authReducer.isLoggedIn
  );
  const [competitionConsent] = useCompetitionConsentStore();

  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isBarVisible, setBarVisible] = useState(
    () => !isPromoHidden(BAR_HIDDEN_KEY)
  );
  const [wasModalShown, setModalShown] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isAllowedRoute = isPromoRoute(location.pathname);
  // Belépett felhasználónál a nyereményjáték kötelező ablaka elsőbbséget élvez:
  // amíg az állapota nincs betöltve, vagy válaszra vár, nem nyitunk mellé másikat.
  const isConsentPending =
    isLoggedIn &&
    (competitionConsent === null || shouldAskConsent(competitionConsent));

  useEffect(() => {
    if (
      wasModalShown ||
      isMobile ||
      !isAllowedRoute ||
      isConsentPending ||
      isPromoHidden(MODAL_HIDDEN_KEY)
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      setModalShown(true);
      setModalOpen(true);
    }, MODAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [wasModalShown, isMobile, isAllowedRoute, isConsentPending]);

  function closeModal(days: number) {
    hidePromoForDays(MODAL_HIDDEN_KEY, days);
    setModalOpen(false);
  }

  function closeBar(days: number) {
    hidePromoForDays(BAR_HIDDEN_KEY, days);
    setBarVisible(false);
  }

  const showBar = isMobile && isAllowedRoute && isBarVisible;

  return (
    <>
      {isModalOpen && !isMobile && (
        <AppPromoModal
          isOpen
          onClose={() => closeModal(DISMISS_DAYS)}
          onStoreClick={() => closeModal(STORE_CLICK_DAYS)}
        />
      )}
      {showBar && (
        <>
          {/* A sáv fix pozíciójú — a lap alján ennyi hely kell, hogy a
              lábléc utolsó sora ne maradjon alatta. */}
          <Box height="160px" flexShrink={0} />
          <AppPromoBar
            onClose={() => closeBar(DISMISS_DAYS)}
            onStoreClick={() => closeBar(STORE_CLICK_DAYS)}
          />
        </>
      )}
    </>
  );
}

export default AppPromo;
